import React from 'react';
import { get, isEmpty, keyBy, set, filter, map, size, split, includes } from 'lodash';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { custom } from 'utils/Api';
import { addNotification } from 'reapop';
import { FormattedMessage } from 'react-intl';
import {
  INITIALIZE_SUCCESS, INITIALIZE_FAILURE, INITIALIZE_REQUEST,
  SET_PERIOD, FETCH_ENTRIES_FAILURE, FETCH_ENTRIES_REQUEST, FETCH_ENTRIES_SUCCESS,
  PATCH_REQUEST, PATCH_SUCCESS, PATCH_FAILURE,
  POST_REQUEST, POST_SUCCESS, POST_OUT_OF_RANGE_SUCCESS, POST_FAILURE,
  DELETE_REQUEST, DELETE_SUCCESS, DELETE_FAILURE,
  PUNCH_REQUEST, PUNCH_SUCCESS, PUNCH_FAILURE,
  OPEN_ENTRY_SUCCESS, OPEN_ENTRY_REQUEST, OPEN_ENTRY_FAILURE,
} from './constants';
import { isRedDay, numberOfWorkingDays } from './components/holidays';
import { getClockCardEntryTypes, getXapValue, getEntries, patch, post, deleteClockCardEntry, getWorkingHours, getOpenEntry, punch, notifyEntryTypeRule } from './api';

const getDates = (startDate, endDate) => {
  const dateArray = [];
  const currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    dateArray.push({
      dateString: moment(currentDate).format('DD/MM/YY'),
      date: moment(currentDate),
      isWeekend: currentDate.getDay() === 6 || currentDate.getDay() === 0,
      dayOfWeek: moment(currentDate).format('ddd'),
      isRedDay: isRedDay(currentDate),
    });
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dateArray;
};
export const setPeriod = (start, end) => ({
  type: SET_PERIOD,
  start,
  end,
  datesBetween: getDates(start, end),
  workingDays: numberOfWorkingDays(start, end),
});
export const fetchEntries = () => ({
  type: FETCH_ENTRIES_REQUEST,
});

export const fetchEntriesSuccess = entries => ({
  type: FETCH_ENTRIES_SUCCESS,
  entries,
});

export const fetchEntriesFailure = error => ({
  type: FETCH_ENTRIES_FAILURE,
  error,
});

export const requestEntries = (start, end) => async (dispatch, getState) => {
  try {
    dispatch(fetchEntries());
    const {
      loginReducer: {
        user: { DefaultEmployeeDetailID },
      },
    } = getState();
    const entriesRes = get(await custom(getEntries(start, end, DefaultEmployeeDetailID)), 'data.value', []);
    const entries = keyBy(entriesRes, 'ClockCardEntryID');
    dispatch(fetchEntriesSuccess(entries));
  } catch (error) {
    dispatch(FETCH_ENTRIES_FAILURE(error));
  }
};
export const changePeriod = (start, end) => async (dispatch) => {
  dispatch(setPeriod(start.startOf('day'), end.endOf('day')));
  dispatch(requestEntries(start.startOf('day'), end.endOf('day')));
};
export const initializeRequest = () => ({
  type: INITIALIZE_REQUEST,
});

export const initializeSuccess = (entryTypes, periodFrom, workingHours, hideActions, timeEmailTypes) => ({
  type: INITIALIZE_SUCCESS,
  entryTypes,
  periodFrom,
  workingHours,
  hideActions,
  timeEmailTypes,
});

export const initializeFailure = error => ({
  type: INITIALIZE_FAILURE,
  error,
});
export const initialize = () => async (dispatch, getState) => {
  try {
    dispatch(initializeRequest());
    const {
      loginReducer: {
        user: { DefaultEmployeeDetailID },
      },
      WebValuesReducer: { valuesByKey },
    } = getState();
    const requests = [
      custom(getXapValue('Pay.PeriodFrom')),
      custom(getClockCardEntryTypes),
      custom(getWorkingHours(DefaultEmployeeDetailID)),
      custom(getXapValue('Email.TimeRegistration')),
    ];
    const hideActions = get(valuesByKey, ['Hide.TimeActions', 'Value']) === 'true';
    const [xapRes, typesRes, hoursRes, xapTimeEmailRes] = await Promise.all(requests);
    const periodFrom = get(xapRes, 'data.value[0].Value') || 1;
    let startDate = moment().date(periodFrom).add(-1, 'months').startOf('day');
    let endDate = moment().date(periodFrom - 1).endOf('day');
    if (endDate.isSameOrBefore(moment())) {
      startDate = startDate.add(1, 'months');
      endDate = startDate.clone().add(1, 'months').add(-1, 'days').endOf('day');
    }
    const entryTypes = keyBy(get(typesRes, 'data.value', []), 'ClockCardEntryTypeID');
    const workingHours = get(hoursRes, 'data.value');
    const timeEmailTypes = split(get(xapTimeEmailRes, 'data.value[0].Value', null), ',') || [];
    dispatch(initializeSuccess(entryTypes, periodFrom, workingHours, hideActions, timeEmailTypes));
    dispatch(changePeriod(startDate, endDate));
  } catch (error) {
    dispatch(initializeFailure(get(error, 'message')));
  }
};
export const useTimeState = () => useSelector(state => state.timeReducer);
export const patchRequest = () => ({
  type: PATCH_REQUEST,
});

export const patchSuccess = (entry, entryId) => ({
  type: PATCH_SUCCESS,
  entry,
  entryId,
});

export const patchFailure = error => ({
  type: PATCH_FAILURE,
  error,
});

export const patchEntry = (entry, entryId, cb) => async (dispatch) => {
  try {
    dispatch(patchRequest());
    const res = await custom(patch(entry, entryId));
    const data = get(res, 'data', {});
    dispatch(patchSuccess(data, entryId));
    dispatch(addNotification({
      title: <FormattedMessage id="time.notification.patch.title" />,
      status: 'success',
      dismissible: true,
      dismissAfter: 3000,
    }));
    cb();
  } catch (error) {
    dispatch(patchFailure(error));
    const msg = get(error, 'response.data.error.message');
    if (msg) {
      const errorData = JSON.parse(msg);
      dispatch(addNotification({
        title: <FormattedMessage id="time.error" />,
        message: get(errorData, 'Errors[0].UserMessage'),
        status: 'error',
        dismissible: true,
        dismissAfter: 5000,
      }));
    } else {
      dispatch(addNotification({
        title: <FormattedMessage id="time.error" />,
        message: get(error, 'message'),
        status: 'error',
        dismissible: true,
        dismissAfter: 3000,
      }));
    }
  }
};
export const postRequest = () => ({
  type: POST_REQUEST,
});

export const postSuccess = entries => ({
  type: POST_SUCCESS,
  entries,
});
export const postOutOfRangeSuccess = () => ({
  type: POST_OUT_OF_RANGE_SUCCESS,
});
export const postFailure = error => ({
  type: POST_FAILURE,
  error,
});
const sendNotifyEmail = async (detailId, from, to, entryTypes, timeEmailTypes, entry) => {
  try {
    const entryTypeId = get(entry, 'ClockCardEntryTypeID');
    const entryTypeRule = get(entryTypes, [entryTypeId, 'ClockCardEntryTypeRule'], null);
    if (entryTypeRule && includes(timeEmailTypes, entryTypeRule)) {
      await custom(notifyEntryTypeRule(detailId, entryTypeRule, from, to));
    }
  } catch (err) {
    // do nothing if fails
  }
};
export const postEntry = (entry, cb) => async (dispatch, getState) => {
  try {
    dispatch(postRequest());
    const {
      timeReducer: {
        start, end, timeEmailTypes, entryTypes,
      },
      loginReducer: {
        user: { DefaultEmployeeDetailID },
      },
    } = getState();
    const data = get(await custom(post({ ...entry, EmployeeDetailID: DefaultEmployeeDetailID })), 'data');
    const ClockIn = get(entry, 'ClockIn');
    const ClockOut = get(entry, 'ClockOut', null);
    if (end.isBefore(ClockIn) || start.isAfter(ClockIn)) {
      dispatch(postOutOfRangeSuccess());
    } else {
      dispatch(postSuccess(keyBy([data], 'ClockCardEntryID')));
    }
    dispatch(addNotification({
      title: <FormattedMessage id="time.notification.post.title" />,
      status: 'success',
      dismissible: true,
      dismissAfter: 3000,
    }));
    cb();
    if (ClockOut && size(timeEmailTypes) > 0) {
      await sendNotifyEmail(DefaultEmployeeDetailID, ClockIn, ClockOut, entryTypes, timeEmailTypes, entry);
    }
  } catch (error) {
    dispatch(postFailure(error));
    const msg = get(error, 'response.data.error.message');
    if (get(error, 'response.status') === 403) {
      dispatch(addNotification({
        title: <FormattedMessage id="time.error" />,
        message: <FormattedMessage id="Error.AccessDenied" />,
        status: 'error',
        dismissible: true,
        dismissAfter: 3000,
      }));
    } else if (msg) {
      const errorData = JSON.parse(msg);
      const code = get(errorData, 'Errors[0].Code');
      if (code === 1) {
        dispatch(addNotification({
          title: <FormattedMessage id="time.error" />,
          message: <FormattedMessage id="time.error.NotEnoughSickHours.Many" />,
          status: 'error',
          dismissible: true,
          dismissAfter: 5000,
        }));
      } else {
        dispatch(addNotification({
          title: <FormattedMessage id="time.error" />,
          message: get(errorData, 'Errors[0].UserMessage'),
          status: 'error',
          dismissible: true,
          dismissAfter: 5000,
        }));
      }
    } else {
      dispatch(addNotification({
        title: <FormattedMessage id="time.error" />,
        message: get(error, 'message'),
        status: 'error',
        dismissible: true,
        dismissAfter: 3000,
      }));
    }
  }
};

export const postSpan = (entry, baseIn, baseOut, endDate, cb, registerWeekend, registerRedDays) => async (dispatch, getState) => {
  const {
    timeReducer: {
      start, end, timeEmailTypes, entryTypes,
    },
    loginReducer: {
      user: { DefaultEmployeeDetailID },
    },
  } = getState();
  const successResponses = [];
  const getSuccessEntries = data => keyBy(filter(data, e => start.isSameOrBefore(get(e, 'ClockIn')) && end.isSameOrAfter(get(e, 'ClockIn'))), 'ClockCardEntryID');
  try {
    dispatch(postRequest());
    const currentDateIn = new Date(baseIn);
    const currentDateOut = new Date(baseOut);

    const addEntry = async () => successResponses.push(await custom(post({
      ...entry,
      EmployeeDetailID: DefaultEmployeeDetailID,
      ClockIn: moment(currentDateIn),
      ClockOut: moment(currentDateOut),
    })));

    while (currentDateIn <= new Date(endDate)) {
      const isWeekday = !isRedDay(currentDateIn) && currentDateIn.getDay() % 6 !== 0;
      if (isWeekday) {
        // eslint-disable-next-line no-await-in-loop
        await addEntry();
      } else if (isRedDay(currentDateIn) && registerRedDays) {
        // eslint-disable-next-line no-await-in-loop
        await addEntry();
      } else if ((currentDateIn.getDay() % 6 === 0) && registerWeekend) {
        // eslint-disable-next-line no-await-in-loop
        await addEntry();
      }
      currentDateIn.setDate(currentDateIn.getDate() + 1);
      currentDateOut.setDate(currentDateOut.getDate() + 1);
    }

    const entries = getSuccessEntries(map(successResponses, 'data'));
    dispatch(postSuccess(entries));
    dispatch(addNotification({
      title: <FormattedMessage id="time.notification.batch.created" values={{ count: size(successResponses) }} />,
      status: 'success',
      dismissible: true,
      dismissAfter: 3000,
    }));
    cb();
    if (size(timeEmailTypes) > 0) {
      await sendNotifyEmail(DefaultEmployeeDetailID, new Date(baseIn).toISOString(), new Date(endDate).toISOString(), entryTypes, timeEmailTypes, entry);
    }
  } catch (error) {
    const msg = get(error, 'response.data.error.message');
    if (msg) {
      const errorData = JSON.parse(msg);
      const code = get(errorData, 'Errors[0].Code');
      if (code === 1) {
        const entries = getSuccessEntries(map(successResponses, 'data'));
        if (size(successResponses) === 0) {
          dispatch(postFailure(error));
          dispatch(addNotification({
            title: <FormattedMessage id="time.error" />,
            message: <FormattedMessage id="time.error.NotEnoughSickHours" />,
            status: 'error',
            dismissible: true,
            dismissAfter: 5000,
          }));
        } else {
          dispatch(postSuccess(entries));
          dispatch(addNotification({
            title: <FormattedMessage id="time.notification.batch.created" values={{ count: size(successResponses) }} />,
            status: 'success',
            dismissible: true,
            dismissAfter: 3000,
          }));
          dispatch(addNotification({
            title: <FormattedMessage id="time.error" />,
            message: <FormattedMessage id="time.error.NotEnoughSickHours.Many" />,
            status: 'error',
            dismissible: true,
            dismissAfter: 5000,
          }));
        }
      } else {
        dispatch(addNotification({
          title: <FormattedMessage id="time.error" />,
          message: get(errorData, 'Errors[0].UserMessage'),
          status: 'error',
          dismissible: true,
          dismissAfter: 5000,
        }));
      }
      cb();
    } else {
      dispatch(postFailure(error));
      dispatch(addNotification({
        title: <FormattedMessage id="time.error" />,
        message: get(error, 'message'),
        status: 'error',
        dismissible: true,
        dismissAfter: 3000,
      }));
    }
  }
};

export const deleteRequest = () => ({
  type: DELETE_REQUEST,
});

export const deleteSuccess = entries => ({
  type: DELETE_SUCCESS,
  entries,
});

export const deleteFailure = error => ({
  type: DELETE_FAILURE,
  error,
});

export const deleteEntry = entryId => async (dispatch, getState) => {
  try {
    dispatch(deleteRequest());
    await custom(deleteClockCardEntry(entryId));
    const { timeReducer: { entries } } = getState();
    delete entries[entryId];
    dispatch(deleteSuccess(entries));
    dispatch(addNotification({
      title: <FormattedMessage id="time.notification.delete.title" />,
      status: 'success',
      dismissible: true,
      dismissAfter: 3000,
    }));
  } catch (error) {
    dispatch(deleteFailure(error));
    const msg = get(error, 'response.data.error.message');
    if (msg) {
      const errorData = JSON.parse(msg);
      dispatch(addNotification({
        title: <FormattedMessage id="time.error" />,
        message: get(errorData, 'Errors[0].UserMessage'),
        status: 'error',
        dismissible: true,
        dismissAfter: 5000,
      }));
    } else {
      dispatch(addNotification({
        title: <FormattedMessage id="time.error" />,
        message: get(error, 'message'),
        status: 'error',
        dismissible: true,
        dismissAfter: 3000,
      }));
    }
  }
};

export const punchInOutRequest = () => ({
  type: PUNCH_REQUEST,
});

export const punchInOutSuccess = (openEntry, isPunchedIn) => ({
  type: PUNCH_SUCCESS,
  openEntry,
  isPunchedIn,
});

export const punchInOutFailure = error => ({
  type: PUNCH_FAILURE,
  error,
});
export const punchInOut = () => async (dispatch, getState) => {
  try {
    dispatch(punchInOutRequest());
    const {
      loginReducer: {
        user: { DefaultEmployeeDetailID },
      },
      WebValuesReducer: { valuesByKey },
    } = getState();
    const kioskId = get(valuesByKey, ['PunchClock.KioskID', 'Value'], null);
    if (!kioskId) throw Error('KioskID missing');
    const entryRes = await custom(punch(DefaultEmployeeDetailID, kioskId));
    const entry = get(entryRes, 'data');
    const isPunchedIn = !get(entry, 'ClockOut');
    dispatch(punchInOutSuccess(entry, isPunchedIn));
    if (!isPunchedIn) dispatch(patchSuccess(entry, get(entry, 'ClockCardEntryID')));
  } catch (err) {
    const message = get(err, 'error.message');
    if (message) set(err, 'message', message);
    dispatch(punchInOutFailure(get(err, 'message')));
    dispatch(addNotification({
      title: <FormattedMessage id="time.error" />,
      message: get(err, 'message'),
      status: 'error',
      dismissible: true,
      dismissAfter: 3000,
    }));
  }
};

export const openEntryRequest = () => ({
  type: OPEN_ENTRY_REQUEST,
});

export const openEntrySuccess = (openEntry, isPunchedIn) => ({
  type: OPEN_ENTRY_SUCCESS,
  openEntry,
  isPunchedIn,
});

export const openEntryFailure = error => ({
  type: OPEN_ENTRY_FAILURE,
  error,
});

export const fetchOpenEntry = () => async (dispatch, getState) => {
  try {
    dispatch(openEntryRequest());
    const {
      loginReducer: {
        user: { DefaultEmployeeDetailID },
      },
    } = getState();
    const entryRes = await custom(getOpenEntry(DefaultEmployeeDetailID));
    const entry = get(entryRes, 'data.value[0]', {});
    const isPunchedIn = (!isEmpty(entry) && !get(entry, 'ClockOut'));
    dispatch(openEntrySuccess(entry, isPunchedIn));
  } catch (err) {
    dispatch(openEntryFailure());
  }
};



// WEBPACK FOOTER //
// ./src/sites/time/actions.js